import React from "react";
import { Redirect } from "react-router";
import { Route, Switch } from "react-router-dom";
import { useSetApiMode } from "../hooks/useSetApiMode";
import { AcceptInvitation } from "../pages/auth/AcceptInvitationPage";
import { AdditionalPrivacyLanguagePage } from "../pages/get-compliant/consumer-collection/AdditionalPrivacyLanguagePage";
import { DataRetentionCookieScanner } from "../pages/get-compliant/consumer-collection/ConsumerCollectionCookieScanner";
import { ConsumerCollectionGdprLawfulBases } from "../pages/get-compliant/consumer-collection/ConsumerCollectionGdprLawfulBases";
import { ConsumerCollectionPurposes } from "../pages/get-compliant/consumer-collection/ConsumerCollectionPurposes";
import { CorePrivacyConceptsPage } from "../pages/get-compliant/consumer-collection/CorePrivacyConceptsPage";
import { GDPRInternationalDataTransfers } from "../pages/get-compliant/consumer-collection/GDPRInternationalDataTransfers";
import { CookiePolicyPage } from "../pages/get-compliant/CookiePolicyPage";
import { ConsumerDataSourcesPage } from "../pages/get-compliant/data-map/ConsumerDataSourcesPage";
import { AddDataRecipients } from "../pages/get-compliant/data-recipients/AddDataRecipients";
import { ConsumerDataDisclosuresPage } from "../pages/get-compliant/data-map/ConsumerDataDisclosuresPage";
import { VendorSelling as DataRecipientVendorSelling } from "../pages/get-compliant/data-recipients/Selling";
import { VendorSharing as DataRecipientVendorSharing } from "../pages/get-compliant/data-recipients/Sharing";
import { ThirdPartyRecipientsPage } from "../pages/get-compliant/data-recipients/ThirdPartyRecipientsPage";
import { EmployeePrivacyNoticeReview } from "../pages/get-compliant/employee-collection/EmployeePrivacyNoticeReview";
import {
  GetCompliantDashboardPage,
  GetCompliantInviteUsersPage,
} from "../pages/get-compliant/GetCompliantDashboardPage";
import { IntegrationsPage } from "../pages/get-compliant/IntegrationsPage";
import { DomainSetupPage } from "../pages/get-compliant/privacy-center/DomainSetupPage";
import { PrivacyNoticeChecklist } from "../pages/get-compliant/PrivacyNoticeChecklist";
import { Settings } from "../pages/get-compliant/Settings";
import { FourOhFourRoutes } from "./FourOhFourRoutes";
import { Routes } from "./routes";
import { ShopifyIntegrationPage } from "../pages/integrations/ShopifyIntegrationPage";
import { VerifySubdomainPage } from "../pages/get-compliant/privacy-center/VerifySubdomainPage";
import { BusinessSurveyAquiloPage } from "../pages/surveys/BusinessSurveyAquiloPage";

export const GetCompliantPageRouter = () => {
  useSetApiMode("Draft", "GetCompliantPageRouter");
  const defaultRedirectTo = Routes.getCompliant.root;
  return (
    <Switch>
      <Route exact path={Routes.getCompliant.dashboard}>
        <GetCompliantDashboardPage />
      </Route>
      <Route exact path={Routes.getCompliant.inviteUsers}>
        <GetCompliantInviteUsersPage />
      </Route>

      <Route exact path={Routes.getCompliant.businessSurveyAquilo}>
        <BusinessSurveyAquiloPage />
      </Route>

      <Route exact path={Routes.getCompliant.dataCollection.root}>
        <Redirect to={Routes.getCompliant.dataCollection.consumerCollection} />
      </Route>
      <Route exact path={Routes.getCompliant.dataCollection.consumerCollection}>
        <Redirect to={Routes.getCompliant.dataCollection.consumerPurposes} />
      </Route>
      <Route exact path={Routes.getCompliant.dataCollection.gdprLawfulBases}>
        <ConsumerCollectionGdprLawfulBases />
      </Route>

      <Route
        exact
        path={Routes.getCompliant.dataMap.consumerSources}
        component={ConsumerDataSourcesPage}
      />

      <Route exact path={Routes.getCompliant.dataCollection.consumerPurposes}>
        <ConsumerCollectionPurposes />
      </Route>
      <Route exact path={Routes.getCompliant.dataCollection.corePrivacyConcepts}>
        <CorePrivacyConceptsPage />
      </Route>

      {/* Properly redirect the SC-style integrations link from the Dev Instructions page if org is still in GC. */}
      <Route exact path={Routes.privacyCenter.integrations}>
        <Redirect to={Routes.getCompliant.dataRetention.vendorIntegrations} />
      </Route>
      <Route exact path={Routes.getCompliant.dataRetention.vendorIntegrations}>
        <IntegrationsPage />
      </Route>

      <Route exact path={Routes.getCompliant.dataRetention.gdprCookieScanner}>
        <DataRetentionCookieScanner />
      </Route>
      <Route exact path={Routes.getCompliant.dataRetention.cookiePolicy}>
        <CookiePolicyPage />
      </Route>

      <Route exact path={Routes.getCompliant.dataCollection.additionalPrivacyLanguage}>
        <AdditionalPrivacyLanguagePage />
      </Route>

      <Route exact path={Routes.getCompliant.dataCollection.internationalDataTransfers}>
        <GDPRInternationalDataTransfers />
      </Route>

      <Route
        exact
        path={[Routes.getCompliant.employeeMap.employeeReviewForGroup]}
        component={EmployeePrivacyNoticeReview}
      />

      <Route exact path={Routes.getCompliant.dataRecipients.root}>
        <AddDataRecipients />
      </Route>

      <Route
        exact
        path={Routes.getCompliant.dataRecipients.dataDisclosures}
        component={ConsumerDataDisclosuresPage}
      />

      <Route
        exact
        path={Routes.getCompliant.dataRecipients.thirdPartyRecipients}
        component={ThirdPartyRecipientsPage}
      />

      <Route exact path={Routes.getCompliant.dataRecipients.sharing}>
        <DataRecipientVendorSharing />
      </Route>
      <Route exact path={Routes.getCompliant.dataRecipients.selling}>
        <DataRecipientVendorSelling />
      </Route>

      <Route exact path={Routes.getCompliant.publishNotices.root}>
        <PrivacyNoticeChecklist />
      </Route>

      <Route exact path={Routes.getCompliant.privacyRequests.privacyCenter}>
        <Redirect to={Routes.getCompliant.privacyCenter.domainSetup} />
      </Route>
      <Route exact path={Routes.getCompliant.privacyCenter.domainSetup}>
        <DomainSetupPage />
      </Route>
      <Route exact path={Routes.getCompliant.privacyCenter.verifySubdomain}>
        <VerifySubdomainPage />
      </Route>

      <Route exact path={Routes.getCompliant.settings.user}>
        <Settings />
      </Route>
      <Route exact path={Routes.getCompliant.settings.root}>
        <Redirect to={Routes.getCompliant.settings.user} />
      </Route>
      <Route exact path={Routes.getCompliant.root}>
        <Redirect to={Routes.getCompliant.dashboard} />
      </Route>
      <Route exact path={Routes.invite} component={AcceptInvitation} />
      <Route exact path={Routes.requests.active}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.login}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.auth.mfa}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.auth.reset}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.auth.set}>
        <Redirect to={defaultRedirectTo} />
      </Route>
      <Route exact path={Routes.root}>
        <Redirect to={defaultRedirectTo} />
      </Route>

      <Route exact path={Routes.integrations.shopify} render={() => <ShopifyIntegrationPage />} />
      <FourOhFourRoutes />
    </Switch>
  );
};
